/**
 * OnboardingTourAdmin (PR #100) — tour específico pro painel Admin.
 *
 * Auto-displays na primeira visita a /admin (não em /admin/curadoria — esse
 * é do Mediador). Usa STORAGE_KEY próprio.
 *
 * 6 steps do admin tributário (ACORDEI! Empresas):
 * 1. Bem-vindo (visão geral das telas)
 * 2. Casos & validação (ficha + motor + fluxo RFB + fee)
 * 3. Financeiro & honorários (fees, faixas, captador)
 * 4. Cadastros (mediadores, cessionária Art Produções, clientes)
 * 5. Métricas & saúde (visão completa, funil, cohort, analytics)
 * 6. Operacional (erros JS, pulso, health)
 */
import { useState, useEffect } from "react";
import {
  X,
  ChevronRight,
  ChevronLeft,
  LayoutDashboard,
  Users,
  GitBranch,
  ScrollText,
  AlertTriangle,
  DollarSign,
} from "lucide-react";

const STORAGE_KEY = "acordei_onboarding_admin_done_v1";

const STEPS = [
  {
    icon: LayoutDashboard,
    color: "#22d3ee",
    title: "Bem-vindo ao Admin",
    subtitle: "Recuperação tributária",
    content:
      "O painel reúne as telas do ACORDEI! Empresas:\n• Casos & validação — fila + ficha completa\n• Financeiro — fees e repasses\n• Configurações — honorários por faixa\n• Cadastros — mediadores, cessionária, clientes\n• Métricas — visão completa, funil, cohort",
    highlight: "Tudo agregado, sem PII. Foco na operação tributária.",
  },
  {
    icon: ScrollText,
    color: "#fbbf24",
    title: "Casos & validação",
    subtitle: "O coração da operação",
    content:
      "Abra a ficha completa de cada caso:\n• Auditoria do motor (PIS/COFINS monofásico)\n• Fluxo RFB — os 2 gates do contador (submissão + constituição)\n• Status de cobrança do fee da plataforma\n• Linha do tempo + documentos + propostas",
    highlight: "O sistema sugere; o contador revisa, assina e protocola.",
  },
  {
    icon: DollarSign,
    color: "#34d399",
    title: "Financeiro & honorários",
    subtitle: "Fees e repasses",
    content:
      "• Financeiro — fees da plataforma + relatório contábil (CSV)\n• Configurações — tabela de honorários REGRESSIVA por faixa (fonte única) + percentuais do captador\n• Honorário só de êxito, decomposto (fee plataforma × honorário do contador), cobrança separada",
    highlight: "Faixa 1 até R$ 100 mil = 30%; cai conforme o volume sobe.",
  },
  {
    icon: Users,
    color: "#60a5fa",
    title: "Cadastros",
    subtitle: "Mediadores, cessionária e clientes",
    content:
      "• Mediadores — quem valida os casos\n• Cessionária — a Art Produções e Distribuição Ltda. (quem assina os contratos)\n• Clientes — as empresas e os contadores parceiros",
    highlight: "Quem assina os contratos é sempre a Art Produções, por Arthur Valle.",
  },
  {
    icon: GitBranch,
    color: "#a78bfa",
    title: "Métricas & saúde",
    subtitle: "Visão completa, funil, cohort",
    content:
      "• Visão completa — casos, ações, custos e produtividade\n• Funil — onde os casos travam por etapa\n• Cohort — retenção por coorte mensal\n• Analytics — visitas e conversão do site",
    highlight: "Tudo agregado. Use pra achar gargalo e medir conversão.",
  },
  {
    icon: AlertTriangle,
    color: "#f472b6",
    title: "Operacional",
    subtitle: "Saúde do sistema",
    content:
      "• /admin/erros — telemetria JS do frontend (Sentry-style)\n• Pulso — custo Claude (mês / 24h / modelo) + status dos serviços\n• /health/* — status de cada serviço externo",
    highlight: "Olhe /admin/erros de manhã pra pegar regressões cedo.",
  },
];

export default function OnboardingTourAdmin() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const done = localStorage.getItem(STORAGE_KEY);
    // Só mostra em rotas /admin que NÃO sejam /admin/curadoria
    const path = window.location.pathname;
    const isAdminButNotCuradoria =
      path.startsWith("/admin") && !path.startsWith("/admin/curadoria");
    if (!done && isAdminButNotCuradoria) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, step]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
    else dismiss();
  };

  const prev = () => setStep((s) => Math.max(0, s - 1));

  if (!visible) return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-admin-title"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        backdropFilter: "blur(4px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => e.target === e.currentTarget && dismiss()}
    >
      <div
        style={{
          background: "#0f172a",
          border: "1px solid #1e293b",
          borderRadius: "20px",
          width: "100%",
          maxWidth: "480px",
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ height: "3px", background: "#1e293b" }}>
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${current.color}, ${current.color}99)`,
              transition: "width 0.4s cubic-bezier(0.23, 1, 0.32, 1)",
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px 0",
          }}
        >
          <span style={{ fontSize: "12px", color: "#475569", fontWeight: 600 }}>
            <span style={{ color: "#22d3ee" }}>Admin</span> · {step + 1} de {STEPS.length}
          </span>
          <button
            onClick={dismiss}
            aria-label="Fechar tour"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#475569",
              padding: "4px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: "16px 24px 24px" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: `${current.color}20`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <Icon size={24} color={current.color} />
          </div>

          <h2
            id="onboarding-admin-title"
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#f1f5f9",
              margin: "0 0 4px",
            }}
          >
            {current.title}
          </h2>
          <p style={{ fontSize: "13px", color: "#64748b", margin: "0 0 16px" }}>
            {current.subtitle}
          </p>

          <p
            style={{
              fontSize: "14px",
              color: "#cbd5e1",
              lineHeight: 1.6,
              margin: "0 0 16px",
              whiteSpace: "pre-line",
            }}
          >
            {current.content}
          </p>

          {current.highlight && (
            <div
              style={{
                padding: "12px 14px",
                borderRadius: "10px",
                background: `${current.color}10`,
                border: `1px solid ${current.color}30`,
                fontSize: "13px",
                color: current.color,
                fontWeight: 600,
              }}
            >
              {current.highlight}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            borderTop: "1px solid #1e293b",
            background: "#020617",
          }}
        >
          <button
            onClick={prev}
            disabled={step === 0}
            style={{
              background: "none",
              border: "none",
              color: step === 0 ? "#1e293b" : "#94a3b8",
              cursor: step === 0 ? "not-allowed" : "pointer",
              padding: "8px 12px",
              fontSize: "14px",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <ChevronLeft size={16} /> Voltar
          </button>

          <button
            onClick={next}
            style={{
              background: `linear-gradient(135deg, ${current.color}, ${current.color}dd)`,
              border: "none",
              color: "#0f172a",
              cursor: "pointer",
              padding: "10px 18px",
              fontSize: "14px",
              fontWeight: 700,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {step === STEPS.length - 1 ? "Começar" : "Próximo"}
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
